"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

interface RateLimitContextType {
  isRateLimited: boolean;
  resetTime: number | null;
  setRateLimited: (retryAfterSeconds: number) => void;
}

const RateLimitContext = createContext<RateLimitContextType | undefined>(undefined);

export const RateLimitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [resetTime, setResetTime] = useState<number | null>(null);

  const setRateLimited = useCallback((retryAfterSeconds: number) => {
    const now = Date.now();
    const resetTimestamp = now + retryAfterSeconds * 1000;

    setIsRateLimited(true);
    setResetTime(resetTimestamp);

    setTimeout(() => {
      setIsRateLimited(false);
      setResetTime(null);
    }, retryAfterSeconds * 1000);
  }, []);

  return (
    <RateLimitContext.Provider value={{ isRateLimited, resetTime, setRateLimited }}>
      {children}
    </RateLimitContext.Provider>
  );
};

export const useRateLimit = (): RateLimitContextType => {
  const context = useContext(RateLimitContext);
  if (context === undefined) {
    throw new Error("useRateLimit must be used within a RateLimitProvider");
  }
  return context;
};
